import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://yytoqcneczoxwegwtuep.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl5dG9xY25lY3pveHdlZ3d0dWVwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgwODM1MDgsImV4cCI6MjA4MzY1OTUwOH0.AapY3VenT2om6UHhxa5KmM4UxeilQ_DRsct2ubzi1Gs';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Types for our database
export interface DeckRecord {
    id: string;
    name: string;
    format: string;
    colors: string[];
    cards: Record<string, number>;
    lands: Record<string, number>;
    sideboard: Record<string, number>;
    commander: { name: string; count: number } | null;
    companion: { name: string; count: number } | null;
    // Plan configurations: { cardName: { base: qty, M1: qty, M2: qty, ... } }
    plans: Record<string, Record<string, number>> | null;
    author_name: string;
    author_id: string | null;
    author_role?: string; // 'user' | 'member' | 'mod' | 'admin'
    author_is_streamer?: boolean;
    author_is_live?: boolean;
    user_id: string | null;
    description: string;
    cover_image: string | null;
    views: number;
    copies: number;
    likes: number;
    upvotes: number;
    downvotes: number;
    is_public: boolean;
    deck_size: number;
    land_count: number;
    mana_curve: number;
    deck_code: string | null; // unique permalink code e.g. "A1b2C3d4"
    created_at: string;
    updated_at: string;
}

// Generate a random 8-char alphanumeric deck code
function generateDeckCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
}

export interface CommentRecord {
    id: string;
    deck_id: string;
    user_id: string | null;
    author_name: string;
    content: string;
    created_at: string;
}

// Helper functions for deck operations
export const deckService = {
    // Get all public decks
    async getPublicDecks(limit = 50, offset = 0) {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('is_public', true)
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);

        if (error) throw error;
        return data as DeckRecord[];
    },

    // Get decks by user
    async getUserDecks(userId: string) {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data as DeckRecord[];
    },

    // Get public decks by a specific user (for profile views)
    async getPublicDecksByUser(userId: string) {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('user_id', userId)
            .eq('is_public', true)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data as DeckRecord[];
    },

    // Get popular decks (most views)
    async getPopularDecks(limit = 10) {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('is_public', true)
            .order('views', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data as DeckRecord[];
    },

    // Get a single deck by ID
    async getDeck(id: string) {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('id', id)
            .single();

        if (error) throw error;

        // Increment view count
        await supabase
            .from('decks')
            .update({ views: (data.views || 0) + 1 })
            .eq('id', id);

        return data as DeckRecord;
    },

    // Get a deck by its permalink code (e.g. "ABC12345")
    async getDeckByCode(code: string) {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('deck_code', code)
            .single();

        if (error) throw error;

        // Increment view count
        await supabase
            .from('decks')
            .update({ views: (data.views || 0) + 1 })
            .eq('id', data.id);

        return data as DeckRecord;
    },

    // Save a new deck
    async saveDeck(deck: Partial<DeckRecord>) {
        // Auto-generate deck_code if not provided
        if (!deck.deck_code) {
            deck.deck_code = generateDeckCode();
        }

        const { data, error } = await supabase
            .from('decks')
            .insert([deck])
            .select()
            .single();

        if (error) throw error;

        // Award +2 sparks for saving a deck (non-blocking)
        try {
            const uid = deck.user_id || (await supabase.auth.getUser()).data.user?.id;
            if (uid) {
                await supabase.from('sparks_ledger').insert({
                    user_id: uid,
                    amount: 2,
                    reason: 'deck_save',
                    metadata: { deck_id: data.id, deck_name: data.name }
                });
            }
        } catch (e) { console.warn('[sparks] deck save award failed:', e); }

        return data as DeckRecord;
    },

    // Update a deck
    async updateDeck(id: string, updates: Partial<DeckRecord>) {
        const { data, error } = await supabase
            .from('decks')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data as DeckRecord;
    },

    // Delete a deck
    async deleteDeck(id: string) {
        const { error } = await supabase
            .from('decks')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    // Search decks
    async searchDecks(query: string) {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('is_public', true)
            .ilike('name', `%${query}%`)
            .order('views', { ascending: false })
            .limit(20);

        if (error) throw error;
        return data as DeckRecord[];
    },

    // Copy a deck (increment copy count)
    async copyDeck(id: string) {
        const { data: original, error: fetchError } = await supabase
            .from('decks')
            .select('*')
            .eq('id', id)
            .single();

        if (fetchError) throw fetchError;

        // Increment copy count on original
        await supabase
            .from('decks')
            .update({ copies: (original.copies || 0) + 1 })
            .eq('id', id);

        return original as DeckRecord;
    },

    // Vote on a deck (+1 or -1). Enforces 1 vote per user per deck.
    // Upvotes cost sparks: 4 for own deck, 2 for others. Downvotes and unvoting are free.
    // Returns the user's current vote state and updated counts.
    async voteDeck(deckId: string, vote: 1 | -1): Promise<{
        userVote: 1 | -1 | null;
        thumbsUp: number;
        thumbsDown: number;
        sparkAwarded?: boolean;
        sparkCost?: number;
    }> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Must be signed in to vote');

        // Check for existing vote
        const { data: existing } = await supabase
            .from('deck_votes')
            .select('vote')
            .eq('user_id', user.id)
            .eq('deck_id', deckId)
            .maybeSingle();

        // Determine if this is a NEW upvote that costs sparks
        const isNewUpvote = vote === 1 && (!existing || existing.vote !== 1);
        let sparkCost = 0;

        if (isNewUpvote) {
            // Check if voting on own deck
            const { data: deck } = await supabase
                .from('decks')
                .select('user_id')
                .eq('id', deckId)
                .single();

            const isSelf = deck?.user_id === user.id;
            sparkCost = isSelf ? 4 : 2;

            // Check balance
            const balance = await sparksService.getBalance(user.id);
            if (balance < sparkCost) {
                throw new Error(`Not enough Sparks (need ${sparkCost}, have ${balance})`);
            }

            // Deduct sparks
            await supabase.from('sparks_ledger').insert({
                user_id: user.id,
                amount: -sparkCost,
                reason: 'deck_vote',
                metadata: { deck_id: deckId, is_self: isSelf }
            });
        }

        if (existing) {
            if (existing.vote === vote) {
                // Same vote again → remove vote (toggle off) — no spark refund
                await supabase
                    .from('deck_votes')
                    .delete()
                    .eq('user_id', user.id)
                    .eq('deck_id', deckId);
            } else {
                // Different vote → update
                await supabase
                    .from('deck_votes')
                    .update({ vote, created_at: new Date().toISOString() })
                    .eq('user_id', user.id)
                    .eq('deck_id', deckId);
            }
        } else {
            // New vote
            await supabase
                .from('deck_votes')
                .insert({ user_id: user.id, deck_id: deckId, vote });
        }

        // Recount votes from deck_votes table
        const { count: upCount } = await supabase
            .from('deck_votes')
            .select('*', { count: 'exact', head: true })
            .eq('deck_id', deckId)
            .eq('vote', 1);

        const { count: downCount } = await supabase
            .from('deck_votes')
            .select('*', { count: 'exact', head: true })
            .eq('deck_id', deckId)
            .eq('vote', -1);

        const thumbsUp = upCount || 0;
        const thumbsDown = downCount || 0;

        // Update denormalized counters on decks table
        await supabase
            .from('decks')
            .update({ upvotes: thumbsUp, downvotes: thumbsDown })
            .eq('id', deckId);

        // Check if we should award a spark to the deck author (every 20 upvotes)
        let sparkAwarded = false;
        if (vote === 1 && thumbsUp > 0 && thumbsUp % 20 === 0) {
            const { data: deck } = await supabase
                .from('decks')
                .select('user_id')
                .eq('id', deckId)
                .single();

            if (deck?.user_id) {
                await supabase
                    .from('sparks_ledger')
                    .insert({
                        user_id: deck.user_id,
                        amount: 1,
                        reason: 'thumbs_up_milestone',
                        metadata: { deck_id: deckId, milestone: thumbsUp }
                    });
                sparkAwarded = true;
            }
        }

        // Get user's current vote
        const { data: currentVote } = await supabase
            .from('deck_votes')
            .select('vote')
            .eq('user_id', user.id)
            .eq('deck_id', deckId)
            .maybeSingle();

        return {
            userVote: currentVote?.vote ?? null,
            thumbsUp,
            thumbsDown,
            sparkAwarded,
            sparkCost
        };
    },

    // Get the current user's vote on a specific deck
    async getUserVote(deckId: string): Promise<1 | -1 | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data } = await supabase
            .from('deck_votes')
            .select('vote')
            .eq('user_id', user.id)
            .eq('deck_id', deckId)
            .maybeSingle();

        return data?.vote ?? null;
    },

    // Get the current user's votes on multiple decks at once
    async getUserVotes(deckIds: string[]): Promise<Record<string, 1 | -1>> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || deckIds.length === 0) return {};

        const { data } = await supabase
            .from('deck_votes')
            .select('deck_id, vote')
            .eq('user_id', user.id)
            .in('deck_id', deckIds);

        const votes: Record<string, 1 | -1> = {};
        (data || []).forEach((row: any) => {
            votes[row.deck_id] = row.vote;
        });
        return votes;
    },

    // Increment view count for a deck
    async incrementViews(id: string) {
        const { data, error } = await supabase
            .from('decks')
            .select('views')
            .eq('id', id)
            .single();

        if (error) throw error;

        await supabase
            .from('decks')
            .update({ views: (data.views || 0) + 1 })
            .eq('id', id);
    }
};

// Comment service
export const commentService = {
    async getComments(deckId: string) {
        const { data, error } = await supabase
            .from('comments')
            .select('*')
            .eq('deck_id', deckId)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data as CommentRecord[];
    },

    async addComment(deckId: string, authorName: string, content: string, userId?: string) {
        const { data, error } = await supabase
            .from('comments')
            .insert([{
                deck_id: deckId,
                author_name: authorName,
                content,
                user_id: userId || null
            }])
            .select()
            .single();

        if (error) throw error;
        return data as CommentRecord;
    },

    async deleteComment(commentId: string) {
        const { error } = await supabase
            .from('comments')
            .delete()
            .eq('id', commentId);

        if (error) throw error;
    }
};

// UUID fallback for non-HTTPS contexts where crypto.randomUUID() isn't available
function generateUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Fallback for non-secure contexts
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// Presence tracking for "here now"
export const presenceService = {
    sessionId: typeof window !== 'undefined'
        ? sessionStorage.getItem('delta-session-id') || generateUUID()
        : 'server',

    async heartbeat() {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('delta-session-id', this.sessionId);
        }

        const { error } = await supabase
            .from('active_users')
            .upsert({
                session_id: this.sessionId,
                last_seen: new Date().toISOString()
            }, {
                onConflict: 'session_id'
            });

        if (error) console.error('Presence heartbeat failed:', error);
    },

    async getActiveCount() {
        // Count users seen in the last 2 minutes
        const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();

        const { count, error } = await supabase
            .from('active_users')
            .select('*', { count: 'exact', head: true })
            .gte('last_seen', twoMinutesAgo);

        if (error) {
            console.error('Active count failed:', error);
            return 1; // At least you're here
        }

        return count || 1;
    },

    async cleanup() {
        // Remove stale sessions (older than 5 minutes)
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();

        await supabase
            .from('active_users')
            .delete()
            .lt('last_seen', fiveMinutesAgo);
    }
};

// Auth service for user management
export const authService = {
    // Sync linked OAuth identities to user_links table
    async syncUserLinks(user: any) {
        if (!user || !user.identities) return;
        const twitchIdentity = user.identities.find((i: any) => i.provider === 'twitch');
        if (twitchIdentity) {
            const twitchUserId = twitchIdentity.id || twitchIdentity.identity_data?.sub;
            const twitchName = twitchIdentity.identity_data?.preferred_username || twitchIdentity.identity_data?.name;
            if (twitchUserId) {
                try {
                    await supabase.from('user_links').upsert({
                        supabase_user_id: user.id,
                        twitch_user_id: String(twitchUserId),
                        twitch_display_name: twitchName || null,
                        discord_user_id: `site_${user.id}`,
                        verified: true,
                        verified_at: new Date().toISOString()
                    }, { onConflict: 'twitch_user_id' });
                } catch { /* OK */ }
            }
        }
    },

    // Get current user
    async getCurrentUser() {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) this.syncUserLinks(user);
        return user;
    },

    // Sign up with email
    async signUpEmail(email: string, password: string, displayName?: string) {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    display_name: displayName || email.split('@')[0]
                }
            }
        });
        if (error) throw error;
        return data;
    },

    // Sign in with email
    async signInEmail(email: string, password: string) {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });
        if (error) throw error;
        return data;
    },

    // Sign in with OAuth (Discord, Google, Twitch, etc.)
    async signInOAuth(provider: 'discord' | 'google' | 'twitter' | 'twitch') {
        const { data, error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined
            }
        });
        if (error) throw error;
        return data;
    },

    // Link additional OAuth provider to existing account (for Twitch verification, etc.)
    // IMPORTANT: Uses linkIdentity() which LINKS to existing account instead of creating a new session
    async linkOAuthProvider(provider: 'discord' | 'google' | 'twitter' | 'twitch') {
        const scopes: Record<string, string> = {
            twitch: 'user:read:email',
            discord: 'identify email',
            google: 'email profile',
            twitter: 'users.read tweet.read'
        };

        console.log(`🔗 Attempting to link ${provider}...`);

        const { data, error } = await supabase.auth.linkIdentity({
            provider,
            options: {
                redirectTo: typeof window !== 'undefined' ? `${window.location.origin}?provider_linked=${provider}` : undefined,
                scopes: scopes[provider]
            }
        });

        console.log(`🔗 linkIdentity response:`, { data, error });

        if (error) {
            console.error(`❌ linkIdentity error:`, error);
            throw error;
        }

        // If linkIdentity returns a URL, we need to manually redirect
        if (data?.url) {
            console.log(`🔗 Redirecting to: ${data.url}`);
            window.location.href = data.url;
        }

        return data;
    },

    // Legacy method for backwards compatibility - now properly links instead of creating new account
    async linkTwitchAccount() {
        return this.linkOAuthProvider('twitch');
    },

    // Get all linked identities for the current user
    async getLinkedIdentities() {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) throw error;
        if (!user) return [];

        // Return the list of connected identity providers
        return user.identities || [];
    },

    // Unlink an OAuth provider from the current account
    async unlinkIdentity(identityId: string) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const identity = user.identities?.find(i => i.id === identityId);
        if (!identity) throw new Error('Identity not found');

        const { error } = await supabase.auth.unlinkIdentity(identity);
        if (error) throw error;
    },

    // Check if a specific provider is linked
    async isProviderLinked(provider: 'discord' | 'google' | 'twitter' | 'twitch') {
        const identities = await this.getLinkedIdentities();
        return identities.some(i => i.provider === provider);
    },

    // Sign out — clear all cached data to keep devices lean
    async signOut() {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;

        // Wipe local caches so nothing lingers after logout
        try { localStorage.clear(); } catch { /* OK */ }
        try { sessionStorage.clear(); } catch { /* OK */ }

        // Clear Cache API entries (service-worker caches, etc.)
        if ('caches' in window) {
            try {
                const keys = await caches.keys();
                await Promise.all(keys.map(k => caches.delete(k)));
            } catch { /* OK */ }
        }
    },

    // Listen for auth changes
    onAuthStateChange(callback: (user: any) => void) {
        return supabase.auth.onAuthStateChange((event, session) => {
            callback(session?.user || null);
        });
    },

    // Resend verification email
    async resendVerification(email: string) {
        const { error } = await supabase.auth.resend({
            type: 'signup',
            email,
            options: {
                emailRedirectTo: typeof window !== 'undefined' ? window.location.origin : undefined
            }
        });
        if (error) throw error;
    }
};

// Overlay card preview service - for OBS overlay integration
export const overlayService = {
    channel: null as ReturnType<typeof supabase.channel> | null,

    // Initialize the channel (call once on app start)
    init() {
        if (!this.channel) {
            this.channel = supabase.channel('overlay-card-hover');
            this.channel.subscribe();
        }
        return this.channel;
    },

    // Broadcast hovered card to overlay
    broadcastCard(card: { name: string; imageUrl: string; backImageUrl?: string; cmc?: number; type?: string } | null) {
        if (!this.channel) this.init();
        this.channel?.send({
            type: 'broadcast',
            event: 'card-hover',
            payload: card
        });
    },

    // Subscribe to card hover events (used by overlay)
    onCardHover(callback: (card: any) => void) {
        if (!this.channel) this.init();
        this.channel?.on('broadcast', { event: 'card-hover' }, ({ payload }) => {
            callback(payload);
        });
    }
};

// ============================================================================
// QUEUE & ROULETTE SYSTEM
// ============================================================================

export interface QueueEntry {
    id: string;
    user_id: string | null;
    display_name: string;
    deck_id: string | null;
    deck_name: string;
    deck_colors: string[];
    deck_format: string;
    deck_cards: Record<string, number> | null;
    tickets: number;
    sparks: number;
    ignition_level: number;
    status: 'waiting' | 'selected' | 'played' | 'removed';
    times_selected: number;
    last_selected_at: string | null;
    streamer_id: string | null;
    event_name: string | null;
    created_at: string;
    updated_at: string;
}

export interface MatchResult {
    id: string;
    player1_deck_id: string | null;
    player1_name: string;
    player1_colors: string[];
    player2_deck_id: string | null;
    player2_name: string | null;
    player2_colors: string[];
    format: 'Bo1' | 'Bo3' | 'Bo5' | 'Casual';
    player1_wins: number;
    player1_losses: number;
    match_winner: 0 | 1 | 2;
    streamer_id: string | null;
    event_name: string | null;
    notes: string | null;
    played_at: string;
    created_at: string;
}

export interface MetaSnapshot {
    id: string;
    snapshot_date: string;
    period_type: 'daily' | 'weekly' | 'monthly';
    top_decks: Array<{ deck_id: string; name: string; matches: number; wins: number; win_rate: number }>;
    color_distribution: Record<string, number>;
    avg_deck_curve: number;
    total_matches: number;
    unique_decks: number;
    unique_players: number;
    top_cards: Array<{ name: string; count: number; percentage: number }>;
    format_distribution: Record<string, number>;
    created_at: string;
}

// Queue service for viewer roulette
export const queueService = {
    // Get all entries in the queue (optionally filtered by streamer)
    async getQueue(streamerId?: string) {
        let query = supabase
            .from('queue_entries')
            .select('*')
            .eq('status', 'waiting')
            .order('ignition_level', { ascending: false })
            .order('sparks', { ascending: false })
            .order('created_at', { ascending: true });

        if (streamerId) {
            query = query.eq('streamer_id', streamerId);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data as QueueEntry[];
    },

    // Add a deck to the queue
    async addToQueue(entry: Partial<QueueEntry>) {
        const { data, error } = await supabase
            .from('queue_entries')
            .insert([{
                ...entry,
                status: 'waiting',
                tickets: entry.tickets || 1,
                sparks: 0,
                ignition_level: 0,
                times_selected: 0,
            }])
            .select()
            .single();

        if (error) throw error;
        return data as QueueEntry;
    },

    // Mark an entry as selected (triggers spark accumulation for others via DB trigger)
    async selectEntry(entryId: string) {
        const { data, error } = await supabase
            .from('queue_entries')
            .update({
                status: 'selected',
                times_selected: supabase.rpc('increment', { row_id: entryId, col: 'times_selected' }),
                last_selected_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            })
            .eq('id', entryId)
            .select()
            .single();

        if (error) throw error;
        return data as QueueEntry;
    },

    // Mark entry as played (after match completes)
    async markPlayed(entryId: string) {
        const { error } = await supabase
            .from('queue_entries')
            .update({
                status: 'played',
                updated_at: new Date().toISOString(),
            })
            .eq('id', entryId);

        if (error) throw error;
    },

    // Remove an entry from queue
    async removeFromQueue(entryId: string) {
        const { error } = await supabase
            .from('queue_entries')
            .update({
                status: 'removed',
                updated_at: new Date().toISOString(),
            })
            .eq('id', entryId);

        if (error) throw error;
    },

    // Get weighted random selection for roulette
    async getWeightedSelection(streamerId?: string): Promise<QueueEntry | null> {
        const queue = await this.getQueue(streamerId);
        if (queue.length === 0) return null;

        // Calculate weights: base + sparks + ignition bonus
        const weights = queue.map(entry => {
            const base = entry.tickets;
            const sparkBonus = entry.sparks * 0.5;
            const ignitionBonus = entry.ignition_level * 10;
            return base + sparkBonus + ignitionBonus;
        });

        const totalWeight = weights.reduce((a, b) => a + b, 0);
        let random = Math.random() * totalWeight;

        for (let i = 0; i < queue.length; i++) {
            random -= weights[i];
            if (random <= 0) {
                return queue[i];
            }
        }

        return queue[queue.length - 1]; // Fallback
    },

    // Subscribe to queue changes in real-time
    subscribeToQueue(streamerId: string | null, callback: (entries: QueueEntry[]) => void) {
        const channel = supabase
            .channel('queue-changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'queue_entries',
                    filter: streamerId ? `streamer_id=eq.${streamerId}` : undefined,
                },
                async () => {
                    const queue = await this.getQueue(streamerId || undefined);
                    callback(queue);
                }
            )
            .subscribe();

        return () => channel.unsubscribe();
    },
};

// Meta tracking service
export const metaService = {
    // Record a match result
    async recordMatch(result: Partial<MatchResult>) {
        const { data, error } = await supabase
            .from('match_results')
            .insert([{
                ...result,
                played_at: result.played_at || new Date().toISOString(),
            }])
            .select()
            .single();

        if (error) throw error;
        return data as MatchResult;
    },

    // Get recent matches
    async getRecentMatches(limit = 20) {
        const { data, error } = await supabase
            .from('match_results')
            .select('*')
            .order('played_at', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data as MatchResult[];
    },

    // Get matches by deck
    async getMatchesByDeck(deckId: string) {
        const { data, error } = await supabase
            .from('match_results')
            .select('*')
            .or(`player1_deck_id.eq.${deckId},player2_deck_id.eq.${deckId}`)
            .order('played_at', { ascending: false });

        if (error) throw error;
        return data as MatchResult[];
    },

    // Get latest meta snapshot
    async getLatestSnapshot() {
        const { data, error } = await supabase
            .from('meta_snapshots')
            .select('*')
            .order('snapshot_date', { ascending: false })
            .limit(1)
            .single();

        if (error && error.code !== 'PGRST116') throw error;
        return data as MetaSnapshot | null;
    },

    // Get color matchup stats
    async getColorMatchups(colors: string[]) {
        const sortedColors = [...colors].sort();
        const { data, error } = await supabase
            .from('match_results')
            .select('player1_colors, player2_colors, match_winner')
            .or(`player1_colors.cs.{${sortedColors.join(',')}},player2_colors.cs.{${sortedColors.join(',')}}`);

        if (error) throw error;

        // Calculate win rate against each color combo
        const matchups: Record<string, { wins: number; losses: number }> = {};

        data?.forEach(match => {
            const isPlayer1 = JSON.stringify([...match.player1_colors].sort()) === JSON.stringify(sortedColors);
            const opponentColors = isPlayer1 ? match.player2_colors : match.player1_colors;
            const opponentKey = [...opponentColors].sort().join('');

            if (!matchups[opponentKey]) {
                matchups[opponentKey] = { wins: 0, losses: 0 };
            }

            if (match.match_winner === 1 && isPlayer1) {
                matchups[opponentKey].wins++;
            } else if (match.match_winner === 2 && !isPlayer1) {
                matchups[opponentKey].wins++;
            } else if (match.match_winner !== 0) {
                matchups[opponentKey].losses++;
            }
        });

        return matchups;
    },

    // Get deck performance stats
    async getDeckStats(deckId: string) {
        const matches = await this.getMatchesByDeck(deckId);

        let wins = 0;
        let losses = 0;

        matches.forEach(match => {
            const isPlayer1 = match.player1_deck_id === deckId;
            if (match.match_winner === 1 && isPlayer1) {
                wins++;
            } else if (match.match_winner === 2 && !isPlayer1) {
                wins++;
            } else if (match.match_winner !== 0) {
                losses++;
            }
        });

        return {
            total_matches: matches.length,
            wins,
            losses,
            win_rate: matches.length > 0 ? wins / matches.length : 0,
        };
    },

    // ─── Discover Meta: Archetypes ───────────────────────────────
    async getArchetypes(format: string): Promise<MetaArchetypeRow[]> {
        const { data, error } = await supabase
            .from('meta_current_archetypes')
            .select('*')
            .eq('format', format)
            .order('field_share', { ascending: false });
        if (error) { console.error('meta archetypes error:', error); return []; }
        return data || [];
    },

    // ─── Discover Meta: Card Stats ──────────────────────────────
    async getCardStats(format: string): Promise<MetaCardRow[]> {
        const { data, error } = await supabase
            .from('meta_current_cards')
            .select('*')
            .eq('format', format)
            .order('in_hand_wr', { ascending: false });
        if (error) { console.error('meta card stats error:', error); return []; }
        return data || [];
    },

    // ─── Discover Meta: User Watchlist ───────────────────────────
    async getWatchlist(format: string): Promise<WatchlistRow[]> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        const { data, error } = await supabase
            .from('user_threat_watchlist')
            .select('*')
            .eq('user_id', user.id)
            .eq('format', format);
        if (error) { console.error('watchlist error:', error); return []; }
        return data || [];
    },

    async addToWatchlist(format: string, card: { name: string; copies: number; category: string; mana_cost: string; type_line: string; image_url: string }): Promise<WatchlistRow | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;
        const { data, error } = await supabase
            .from('user_threat_watchlist')
            .upsert({ user_id: user.id, format, card_name: card.name, copies: card.copies, category: card.category, mana_cost: card.mana_cost, type_line: card.type_line, image_url: card.image_url }, { onConflict: 'user_id,card_name,format' })
            .select().single();
        if (error) { console.error('watchlist add error:', error); return null; }
        return data;
    },

    async updateWatchlistCard(id: string, updates: Partial<{ copies: number; category: string; meta_share: number }>): Promise<boolean> {
        const { error } = await supabase.from('user_threat_watchlist').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id);
        return !error;
    },

    async removeFromWatchlist(id: string): Promise<boolean> {
        const { error } = await supabase.from('user_threat_watchlist').delete().eq('id', id);
        return !error;
    },

    // ─── Discover Meta: Community Reports ────────────────────────
    async submitMatchReport(report: { format: string; myArchetype: string; opponentArchetype: string; result: 'win' | 'loss' | 'draw'; matchFormat?: 'bo1' | 'bo3'; myDeckId?: string; notes?: string; region?: string; rankTier?: string; }): Promise<{ ok: boolean; error?: string; trustWeight?: number; reportsToday?: number; dailyLimit?: number }> {
        const { data, error } = await supabase.rpc('submit_meta_report', {
            p_format: report.format,
            p_my_archetype: report.myArchetype,
            p_opponent_archetype: report.opponentArchetype,
            p_result: report.result,
            p_match_format: report.matchFormat || 'bo1',
            p_my_deck_id: report.myDeckId || null,
            p_notes: report.notes || null,
            p_region: report.region || null,
            p_rank_tier: report.rankTier || null,
        });
        if (error) { console.error('meta report error:', error); return { ok: false, error: error.message }; }
        const result = data as { ok: boolean; error?: string; trust_weight?: number; reports_today?: number; daily_limit?: number };
        return {
            ok: result.ok,
            error: result.error,
            trustWeight: result.trust_weight,
            reportsToday: result.reports_today,
            dailyLimit: result.daily_limit,
        };
    },

    async getMatchupSummary(format: string) {
        const { data, error } = await supabase.from('meta_matchup_summary').select('*').eq('format', format).order('total_reports', { ascending: false });
        if (error) { console.error('matchup summary error:', error); return []; }
        return data || [];
    },

    /** Trigger the meta data pipeline to refresh from external sources */
    async refreshPipeline(format?: string): Promise<{ ok: boolean; error?: string }> {
        const url = format
            ? `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/meta-data-pipeline?format=${format}`
            : `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/meta-data-pipeline`;
        try {
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || ''}` },
            });
            const json = await res.json();
            return { ok: json.ok, error: json.error };
        } catch (err) {
            return { ok: false, error: String(err) };
        }
    },

    /** Get the timestamp of the last successful meta pipeline run */
    async getLastUpdated(): Promise<string | null> {
        try {
            const { data, error } = await supabase.rpc('get_meta_last_updated');
            if (error) {
                // Fallback: check the most recent archetype snapshot date
                const { data: snap } = await supabase
                    .from('meta_archetype_snapshots')
                    .select('snapshot_date')
                    .order('snapshot_date', { ascending: false })
                    .limit(1)
                    .single();
                return snap?.snapshot_date || null;
            }
            return data;
        } catch {
            return null;
        }
    },

    /** Get recent pipeline run history */
    async getPipelineRuns(limit = 5): Promise<Array<{ id: string; started_at: string; finished_at: string | null; status: string; formats_processed: string[]; triggered_by: string }>> {
        const { data, error } = await supabase
            .from('meta_pipeline_runs')
            .select('id, started_at, finished_at, status, formats_processed, triggered_by')
            .order('started_at', { ascending: false })
            .limit(limit);
        if (error) { console.error('pipeline runs error:', error); return []; }
        return data || [];
    },
};

// ============================================================================
// META DECK SERVICE — Browse, Fork, Track Variants
// ============================================================================

export interface MetaDeckRow {
    id: string;
    name: string;
    format: string;
    color_identity: string[];
    decklist: string;
    cards: any;
    cover_image: string | null;
    is_public: boolean;
    is_meta_reference: boolean;
    archetype_id: string | null;
    parent_deck_id: string | null;
    source: string | null;
    source_url: string | null;
    source_event: string | null;
    source_player: string | null;
    source_date: string | null;
    fork_count: number;
    archetype_name?: string;
    archetype_colors?: string[];
    archetype_tier?: string;
    archetype_win_rate?: number;
    archetype_field_share?: number;
    created_at: string;
}

export const metaDeckService = {
    /** Get meta reference decks for a format, optionally filtered by archetype */
    async getMetaDecks(format: string, archetypeId?: string): Promise<MetaDeckRow[]> {
        let query = supabase
            .from('meta_reference_decks')
            .select('*')
            .eq('archetype_format', format);
        if (archetypeId) {
            query = query.eq('archetype_id', archetypeId);
        }
        const { data, error } = await query.order('source_date', { ascending: false }).limit(50);
        if (error) { console.error('meta decks error:', error); return []; }
        return (data || []) as MetaDeckRow[];
    },

    /** Fork a meta deck into user's personal collection */
    async forkDeck(deckId: string, newName?: string): Promise<{ ok: boolean; id?: string; error?: string }> {
        const { data, error } = await supabase.rpc('fork_meta_deck', {
            p_deck_id: deckId,
            p_new_name: newName || null,
        });
        if (error) { console.error('fork error:', error); return { ok: false, error: error.message }; }
        const result = data as { ok: boolean; id?: string; error?: string };
        return result;
    },

    /** Get all variants (forks) of a meta reference deck */
    async getVariants(parentDeckId: string): Promise<MetaDeckRow[]> {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('parent_deck_id', parentDeckId)
            .order('created_at', { ascending: false });
        if (error) { console.error('variants error:', error); return []; }
        return (data || []) as MetaDeckRow[];
    },

    /** Get user's forked decks */
    async getMyForks(): Promise<MetaDeckRow[]> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('user_id', user.id)
            .not('parent_deck_id', 'is', null)
            .order('created_at', { ascending: false });
        if (error) { console.error('my forks error:', error); return []; }
        return (data || []) as MetaDeckRow[];
    },

    /** Get the parent (original) deck for a fork */
    async getParentDeck(parentDeckId: string): Promise<MetaDeckRow | null> {
        const { data, error } = await supabase
            .from('decks')
            .select('*')
            .eq('id', parentDeckId)
            .single();
        if (error) return null;
        return data as MetaDeckRow;
    },
};

// ============================================================================
// ROLE MANAGEMENT SERVICE
// ============================================================================

export type UserRole = 'admin' | 'mod' | 'lgs_owner' | 'member' | 'user';

export interface UserRoleRecord {
    id: string;
    user_id: string;
    role: UserRole;
    granted_by: string | null;
    granted_at: string;
    notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface ModApplication {
    id: string;
    user_id: string;
    email: string;
    display_name: string;
    reason: string;
    status: 'pending' | 'approved' | 'rejected';
    reviewed_by: string | null;
    reviewed_at: string | null;
    created_at: string;
}

export const roleService = {
    // Get current user's role
    async getCurrentUserRole(): Promise<UserRole> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return 'user';

        const { data, error } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .single();

        if (error || !data) return 'user';
        return data.role as UserRole;
    },

    // Check if current user has at least the required role
    async hasRole(requiredRole: UserRole): Promise<boolean> {
        const currentRole = await this.getCurrentUserRole();
        const hierarchy: Record<UserRole, number> = {
            admin: 4,
            mod: 3,
            lgs_owner: 3,
            member: 2,
            user: 1,
        };
        return hierarchy[currentRole] >= hierarchy[requiredRole];
    },

    // Get all users with roles (admin only)
    async getAllRoles(): Promise<UserRoleRecord[]> {
        const { data, error } = await supabase
            .from('user_roles')
            .select('*')
            .order('role', { ascending: false });

        if (error) throw error;
        return data as UserRoleRecord[];
    },

    // Get mod count
    async getModCount(): Promise<number> {
        const { count, error } = await supabase
            .from('user_roles')
            .select('*', { count: 'exact', head: true })
            .eq('role', 'mod');

        if (error) return 0;
        return count || 0;
    },

    // Assign role to user (admin only)
    async assignRole(userId: string, role: UserRole, notes?: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error } = await supabase
            .from('user_roles')
            .upsert({
                user_id: userId,
                role,
                granted_by: user.id,
                granted_at: new Date().toISOString(),
                notes: notes || null,
                updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });

        if (error) throw error;
    },

    // Remove role (set back to 'user')
    async removeRole(userId: string): Promise<void> {
        const { error } = await supabase
            .from('user_roles')
            .delete()
            .eq('user_id', userId);

        if (error) throw error;
    },

    // Get user by email (for admin lookup)
    async getUserByEmail(email: string): Promise<{ id: string; email: string } | null> {
        // Note: This requires special permissions - direct auth lookup
        // For now, we'll search through users who have roles or decks
        const { data } = await supabase
            .from('decks')
            .select('user_id, author_name')
            .limit(1);

        // This is a workaround - proper implementation needs admin API
        return null;
    },

    // ========================================================================
    // MOD APPLICATION SYSTEM
    // ========================================================================

    // Submit mod application
    async submitModApplication(reason: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error } = await supabase
            .from('mod_applications')
            .insert({
                user_id: user.id,
                email: user.email || '',
                display_name: user.user_metadata?.display_name || user.email?.split('@')[0] || 'Unknown',
                reason,
                status: 'pending',
            });

        if (error) throw error;
    },

    // Get pending applications (admin only)
    async getPendingApplications(): Promise<ModApplication[]> {
        const { data, error } = await supabase
            .from('mod_applications')
            .select('*')
            .eq('status', 'pending')
            .order('created_at', { ascending: true });

        if (error) throw error;
        return data as ModApplication[];
    },

    // Get all applications (admin only)
    async getAllApplications(): Promise<ModApplication[]> {
        const { data, error } = await supabase
            .from('mod_applications')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data as ModApplication[];
    },

    // Approve application (admin only)
    async approveApplication(applicationId: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Get application
        const { data: app, error: fetchError } = await supabase
            .from('mod_applications')
            .select('*')
            .eq('id', applicationId)
            .single();

        if (fetchError || !app) throw new Error('Application not found');

        // Check mod limit (30 max)
        const modCount = await this.getModCount();
        if (modCount >= 30) {
            throw new Error('Maximum mod limit (30) reached');
        }

        // Assign mod role
        await this.assignRole(app.user_id, 'mod', 'Approved via application');

        // Update application status
        const { error } = await supabase
            .from('mod_applications')
            .update({
                status: 'approved',
                reviewed_by: user.id,
                reviewed_at: new Date().toISOString(),
            })
            .eq('id', applicationId);

        if (error) throw error;
    },

    // Reject application (admin only)
    async rejectApplication(applicationId: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error } = await supabase
            .from('mod_applications')
            .update({
                status: 'rejected',
                reviewed_by: user.id,
                reviewed_at: new Date().toISOString(),
            })
            .eq('id', applicationId);

        if (error) throw error;
    },

    // Check if user has pending application
    async hasPendingApplication(): Promise<boolean> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { data } = await supabase
            .from('mod_applications')
            .select('id')
            .eq('user_id', user.id)
            .eq('status', 'pending')
            .single();

        return !!data;
    },
};

// ============================================================================
// BUG REPORT SERVICE
// ============================================================================

export interface BugReport {
    id: string;
    user_id: string | null;
    user_email: string | null;
    display_name: string;
    location: string;        // Where in the app (e.g., "Login popup", "Discover tab")
    description: string;     // What happened
    expected: string | null; // What should have happened (optional)
    status: 'new' | 'in_progress' | 'resolved' | 'wont_fix';
    resolved_by: string | null;
    resolved_at: string | null;
    resolution_notes: string | null;
    created_at: string;
}

export const bugReportService = {
    // Submit a bug report (any user, including logged in)
    async submitBugReport(report: {
        location: string;
        description: string;
        expected?: string;
    }): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();

        const { error } = await supabase
            .from('bug_reports')
            .insert({
                user_id: user?.id || null,
                user_email: user?.email || null,
                display_name: user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Anonymous',
                location: report.location,
                description: report.description,
                expected: report.expected || null,
                status: 'new',
            });

        if (error) throw error;
    },

    // Get all bug reports (admin/mod only)
    async getAllBugReports(): Promise<BugReport[]> {
        const { data, error } = await supabase
            .from('bug_reports')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data as BugReport[];
    },

    // Get new/open bug reports count
    async getOpenBugCount(): Promise<number> {
        const { count, error } = await supabase
            .from('bug_reports')
            .select('*', { count: 'exact', head: true })
            .in('status', ['new', 'in_progress']);

        if (error) return 0;
        return count || 0;
    },

    // Update bug status (admin/mod only)
    async updateBugStatus(
        bugId: string,
        status: BugReport['status'],
        notes?: string
    ): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const updates: Partial<BugReport> = { status };

        if (status === 'resolved' || status === 'wont_fix') {
            updates.resolved_by = user.id;
            updates.resolved_at = new Date().toISOString();
            updates.resolution_notes = notes || null;
        }

        const { error } = await supabase
            .from('bug_reports')
            .update(updates)
            .eq('id', bugId);

        if (error) throw error;
    },

    // Delete a bug report (admin only)
    async deleteBugReport(bugId: string): Promise<void> {
        const { error } = await supabase
            .from('bug_reports')
            .delete()
            .eq('id', bugId);

        if (error) throw error;
    },

    // Get user's own bug reports
    async getMyBugReports(): Promise<BugReport[]> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await supabase
            .from('bug_reports')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data as BugReport[];
    },
};

// =============================================================================
// Sparks Service — Global site-level spark currency
// =============================================================================
export const sparksService = {
    // Get the user's GLOBAL spark balance (site ledger + all Twitch channel balances)
    async getBalance(userId?: string): Promise<number> {
        try {
            const uid = userId || (await supabase.auth.getUser()).data.user?.id;
            if (!uid) return 0;

            // Try the unified global balance RPC first (includes Twitch channel balances)
            let { data: rpcData, error: rpcError } = await supabase
                .rpc('get_global_spark_balance', { p_user_id: uid });

            if (rpcError) {
                // Try p_supabase_user_id parameter as backup
                const res = await supabase.rpc('get_global_spark_balance', { p_supabase_user_id: uid });
                if (!res.error && res.data !== null) {
                    rpcData = res.data;
                    rpcError = null;
                }
            }

            if (!rpcError && rpcData !== null) {
                return rpcData as number;
            }

            // Fallback: site-only ledger sum
            const { data, error } = await supabase
                .from('sparks_ledger')
                .select('amount')
                .eq('user_id', uid);

            if (error) {
                console.warn('sparks_ledger not available yet:', error.message);
                return 0;
            }

            return (data || []).reduce((sum: number, row: any) => sum + (row.amount || 0), 0);
        } catch {
            return 0;
        }
    },

    // Claim daily sign-in bonus (+1×W sparks, where W = weeks since creation, capped at 7)
    // Tries server-side RPC first; falls back to ledger-checked client path
    async claimDailySignIn(userId?: string): Promise<{ success: boolean; message: string; balance?: number }> {
        try {
            const uid = userId || (await supabase.auth.getUser()).data.user?.id;
            if (!uid) return { success: false, message: 'Not signed in' };

            // Check ledger directly for 24h cooldown (works even if RPC/profile column don't exist)
            const canClaim = await sparksService.canClaimDaily(uid);
            if (!canClaim) {
                return { success: false, message: 'Already claimed today — come back tomorrow!' };
            }

            // Try server-side RPC first (atomic, tamper-proof)
            try {
                const { data, error } = await supabase.rpc('claim_daily_signin', { p_user_id: uid });
                if (!error && data) {
                    const result = data as { success: boolean; error?: string; sparks_awarded?: number; week?: number; next_claim?: string };
                    if (!result.success) {
                        if (result.next_claim) {
                            const nextClaim = new Date(result.next_claim);
                            const hoursLeft = Math.ceil((nextClaim.getTime() - Date.now()) / (1000 * 60 * 60));
                            return { success: false, message: `Come back in ${hoursLeft}h for your daily spark!` };
                        }
                        return { success: false, message: result.error || 'Could not claim daily spark' };
                    }
                    const balance = await sparksService.getBalance(uid);
                    const W = result.sparks_awarded || 1;
                    return { success: true, message: `+${W} Spark${W > 1 ? 's' : ''}! (Week ${result.week || 1} bonus) ⚡`, balance };
                }
            } catch {
                // RPC doesn't exist yet — fall through to fallback
            }

            // Fallback: client-side claim (canClaimDaily already verified above)
            const { data: profile } = await supabase
                .from('profiles')
                .select('created_at')
                .eq('id', uid)
                .single();

            const createdAt = profile?.created_at ? new Date(profile.created_at) : new Date();
            const weeksSinceCreation = Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24 * 7));
            const W = Math.max(1, Math.min(7, weeksSinceCreation + 1));

            const { error: ledgerErr } = await supabase
                .from('sparks_ledger')
                .insert({
                    user_id: uid,
                    amount: W,
                    reason: 'daily_signin',
                    metadata: { claimed_at: new Date().toISOString(), week: W }
                });

            if (ledgerErr) throw ledgerErr;

            // Also update profile timestamp if column exists
            await supabase
                .from('profiles')
                .update({ last_signin_claim: new Date().toISOString() })
                .eq('id', uid);

            const balance = await sparksService.getBalance(uid);
            return { success: true, message: `+${W} Spark${W > 1 ? 's' : ''}! (Week ${W} bonus) ⚡`, balance };
        } catch (err: any) {
            console.warn('Daily sign-in claim failed:', err.message);
            return { success: false, message: 'Could not claim daily spark' };
        }
    },

    // Check if daily sign-in is available — queries sparks_ledger directly
    async canClaimDaily(userId?: string): Promise<boolean> {
        try {
            const uid = userId || (await supabase.auth.getUser()).data.user?.id;
            if (!uid) return false;

            // Check most recent daily_signin entry in ledger
            const { data: lastEntry } = await supabase
                .from('sparks_ledger')
                .select('created_at')
                .eq('user_id', uid)
                .eq('reason', 'daily_signin')
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            if (!lastEntry) return true; // Never claimed before

            const lastClaim = new Date(lastEntry.created_at);
            return (Date.now() - lastClaim.getTime()) / (1000 * 60 * 60) >= 24;
        } catch {
            return false;
        }
    },

    // Award sparks for saving a deck (+2)
    async awardDeckSave(deckId: string): Promise<{ success: boolean; balance?: number }> {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return { success: false };

            await supabase
                .from('sparks_ledger')
                .insert({
                    user_id: user.id,
                    amount: 2,
                    reason: 'deck_save',
                    metadata: { deck_id: deckId }
                });

            const balance = await sparksService.getBalance(user.id);
            return { success: true, balance };
        } catch (err: any) {
            console.warn('Deck save spark award failed:', err.message);
            return { success: false };
        }
    },

    // Award signup bonus (+10 sparks) — one-time only, via RPC
    async awardSignupBonus(): Promise<{ success: boolean; sparks?: number }> {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return { success: false };

            const { data, error } = await supabase.rpc('award_signup_bonus', { p_user_id: user.id });
            if (error) {
                console.warn('Signup bonus failed:', error.message);
                return { success: false };
            }
            return { success: data?.success ?? false, sparks: data?.sparks_awarded };
        } catch {
            return { success: false };
        }
    },

    // Check and award "on the house" bonus (+5 free after first 5 spent)
    async checkOnTheHouse(): Promise<{ awarded: boolean; balance?: number }> {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return { awarded: false };

            // Check if already received on-the-house
            const { data: existing } = await supabase
                .from('sparks_ledger')
                .select('id')
                .eq('user_id', user.id)
                .eq('reason', 'on_the_house')
                .limit(1);

            if (existing && existing.length > 0) return { awarded: false };

            // Check total spent
            const { data: spendRows } = await supabase
                .from('sparks_ledger')
                .select('amount')
                .eq('user_id', user.id)
                .lt('amount', 0);

            const totalSpent = Math.abs((spendRows || []).reduce((sum: number, r: any) => sum + r.amount, 0));

            if (totalSpent >= 5) {
                await supabase
                    .from('sparks_ledger')
                    .insert({
                        user_id: user.id,
                        amount: 5,
                        reason: 'on_the_house',
                        metadata: { trigger: 'first_5_spent', total_spent: totalSpent }
                    });

                const balance = await sparksService.getBalance(user.id);
                return { awarded: true, balance };
            }

            return { awarded: false };
        } catch {
            return { awarded: false };
        }
    },

    // Award first-brew bonus (+5 for first submission to a new channel, max 10 channels)
    async awardFirstBrew(channelId: string): Promise<{ success: boolean; sparks?: number }> {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return { success: false };

            // Check total first-brew claims (max 10)
            const { count } = await supabase
                .from('first_brew_claims')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', user.id);

            if ((count || 0) >= 10) return { success: false };

            // Check if already claimed for this channel
            const { data: existing } = await supabase
                .from('first_brew_claims')
                .select('user_id')
                .eq('user_id', user.id)
                .eq('channel_id', channelId)
                .maybeSingle();

            if (existing) return { success: false };

            // Claim it
            await supabase
                .from('first_brew_claims')
                .insert({ user_id: user.id, channel_id: channelId });

            await supabase
                .from('sparks_ledger')
                .insert({
                    user_id: user.id,
                    amount: 5,
                    reason: 'first_brew',
                    channel_id: channelId,
                    metadata: { channel_id: channelId }
                });

            return { success: true, sparks: 5 };
        } catch {
            return { success: false };
        }
    },

    // Get recent transaction history for the user
    async getTransactionHistory(limit = 20): Promise<Array<{
        id: string;
        amount: number;
        reason: string;
        metadata: any;
        created_at: string;
    }>> {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return [];

            const { data, error } = await supabase
                .from('sparks_ledger')
                .select('id, amount, reason, metadata, created_at')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) throw error;
            return data || [];
        } catch {
            return [];
        }
    },

    // Check friendship milestones (5/10/25/50 friends → spark rewards)
    async checkFriendshipMilestones(): Promise<{ friendCount: number; sparksAwarded: number }> {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return { friendCount: 0, sparksAwarded: 0 };

            const { data, error } = await supabase.rpc('check_friendship_milestones', { p_user_id: user.id });
            if (error) return { friendCount: 0, sparksAwarded: 0 };
            return {
                friendCount: data?.friend_count ?? 0,
                sparksAwarded: data?.sparks_awarded ?? 0,
            };
        } catch {
            return { friendCount: 0, sparksAwarded: 0 };
        }
    },

    // Spend sparks (deduct from balance)
    async spendSparks(amount: number, reason: string, referenceId?: string): Promise<{ success: boolean; balance?: number }> {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return { success: false };

            // Check balance first
            const currentBalance = await sparksService.getBalance();
            if (currentBalance < amount) return { success: false };

            // Insert negative ledger entry
            const { error } = await supabase.from('sparks_ledger').insert({
                user_id: user.id,
                amount: -amount,
                reason,
                metadata: referenceId ? { reference_id: referenceId } : null,
            });
            if (error) return { success: false };

            const newBalance = await sparksService.getBalance();
            return { success: true, balance: newBalance };
        } catch {
            return { success: false };
        }
    },
};

// MetaArchetypeRow and related types are used by MetaPanel
export interface MetaArchetypeRow {
    id: string; name: string; format: string; colors: string[]; tier: string;
    key_cards: string[]; description: string | null;
    field_share: number; win_rate: number; matches: number; trend: string;
    snapshot_date: string | null;
}
export interface MetaCardRow {
    card_name: string; format: string; colors: string[];
    in_hand_wr: number; played_wr: number; included_wr: number;
    total_games: number; snapshot_date: string;
}
export interface WatchlistRow {
    id: string; user_id: string; format: string; card_name: string;
    copies: number; category: string; meta_share: number;
    mana_cost: string; type_line: string; image_url: string;
}
